import { MarketoFormProps } from '@/features/MarketoForm/MarketoForm.interface';
import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { MarketoControlsDistribution } from '@/utils/constants';
import {
  resolveBlacklistUrls,
  resolveBool,
  resolveColorName,
  resolveCRMSyncs,
  resolveImage,
  resolveLink,
  resolveRTC,
  resolveSpacingSize,
  resolveTheme,
  resolveVideo,
} from '@/utils/resolvers';
import React, { memo, useMemo, useRef } from 'react';
import CTA from './CTA';
import { mapTarget, onDatalayer } from './CTA.datalayer';

const MappedCTA = memo(
  ({ content, presentation, contentButtons, pageModels, marketoContent }: any) => {
    const {
      topSpacing,
      bottomSpacing,
      containerTopBackgroundColor,
      containerBottomBackgroundColor,
      includeMarketo,
      theme,
      datalayerVariant,
      ...props
    } = presentation;
    const containerProps = useMemo(
      () => ({
        topSpacing,
        bottomSpacing,
        topBackgroundColor: containerTopBackgroundColor,
        bottomBackgroundColor: containerBottomBackgroundColor,
        datalayerPosition: 'cta_component',
        datalayerVariant,
      }),
      [
        bottomSpacing,
        containerBottomBackgroundColor,
        containerTopBackgroundColor,
        datalayerVariant,
        topSpacing,
      ],
    );

    const {
      config: {
        marketo: {
          recaptcha_sitekey: recaptchaSitekey = '',
          recaptcha_legal: recaptchaLegal = '',
          munchkin_id: munchkinId = '',
          base_url: baseUrl = '',
          email_fieldname: emailFieldname = '',
          email_blacklist_error_message: emailBlacklistErrorMessage = '',
          email_crm_fieldname: emailCRMFieldname = '',
        } = {},
      },
      labels: { marketo: marketoLabels = {} },
    } = pageModels;
    const marketoProps: MarketoFormProps = useMemo(
      () => ({
        ...marketoContent,
        recaptchaSitekey,
        recaptchaLegal,
        munchkinId,
        baseUrl,
        theme,
        outline: false,
        controlsDistribution: MarketoControlsDistribution.SingleColumn,
        emailFieldname,
        emailBlacklistErrorMessage,
        emailCRMFieldname,
        labels: marketoLabels,
      }),
      [
        marketoContent,
        recaptchaSitekey,
        recaptchaLegal,
        munchkinId,
        baseUrl,
        theme,
        emailFieldname,
        emailBlacklistErrorMessage,
        emailCRMFieldname,
        marketoLabels,
      ],
    );

    return (
      <ContentContainer {...containerProps}>
        <MuiContainer className="medium">
          <CTA
            {...content}
            {...props}
            theme={theme}
            contentButtons={contentButtons}
            includeMarketo={includeMarketo}
            marketoProps={marketoProps}
          />
        </MuiContainer>
      </ContentContainer>
    );
  },
);

const CTACMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        logo: [null, resolveImage],
        detailContent: [null, resolveRTC],
        primaryCTA: [null, resolveLink],
        secondaryCTA: [null, resolveLink],
        backgroundImage: [null, resolveImage],
        backgroundVideo: [null, resolveVideo],
        mobileBackgroundImage: [null, resolveImage],
        heading: null,
        superHeading: null,
      },
    ],
    componentParameterMap: [
      'presentation',
      {
        'theme-background-color': ['backgroundColor', resolveColorName],
        'non-theme-background-color': 'nonThemeBackgroundColor',
        'container-bottom-background-color': ['containerBottomBackgroundColor', resolveColorName],
        'container-top-background-color': ['containerTopBackgroundColor', resolveColorName],
        'top-spacing': ['topSpacing', resolveSpacingSize],
        'bottom-spacing': ['bottomSpacing', resolveSpacingSize],
        'text-color': ['theme', resolveTheme('text-color')],
        'include-subscription': ['includeMarketo', resolveBool],
        'variant-label': 'datalayerVariant',
      },
    ],
    marketoDocument: [
      'marketoContent',
      {
        marketoFormId: 'formId',
        confirmationMessage: [null, resolveRTC],
        rules: 'rules',
        heading: 'heading',
        description: ['formDescription', resolveRTC],
        emailBlacklistUrls: ['emailsBlacklisted', resolveBlacklistUrls],
        crmSyncs: ['CRMSyncFieldnames', resolveCRMSyncs],
      },
    ],
  });

  const requiredFields = useRef({
    document: '<b>CTA Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  const MappedCTAGTM = withGTM({
    Component: MappedCTA,
    onDatalayer,
    config: { mapTarget },
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedCTAGTM}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default CTACMSWrapper;
